import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { GraduationCap, MagnifyingGlass, Clock, ArrowRight } from '@phosphor-icons/react';
import { setPageMeta } from '../utils/seo';
import { TUTORIALS, ROLES } from '../data/tutorials';

const roleMeta = (key) => ROLES.find((r) => r.key === key) || ROLES[3];

export default function Tutorials() {
  const [searchParams, setSearchParams] = useSearchParams();
  const role = searchParams.get('role') || '';
  const [query, setQuery] = useState('');

  useEffect(() => {
    setPageMeta({
      title: 'Tutorials — How ConstructHelp Works | ConstructHelp',
      description: 'Step-by-step tutorials for every ConstructHelp feature: posting jobs, escrow payments, finding work, the tools marketplace, invoices, AI lead finder and more.',
      url: `${window.location.origin}/tutorials`,
    });
    window.scrollTo(0, 0);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return TUTORIALS.filter((t) => {
      if (role && t.role !== role) return false;
      if (!q) return true;
      return `${t.title} ${t.summary} ${t.steps.map((s) => s.title).join(' ')}`.toLowerCase().includes(q);
    });
  }, [role, query]);

  return (
    <div className="min-h-screen bg-[#E8E9EB] dark:bg-[#121212]" data-testid="tutorials-page">
      {/* Hero */}
      <div className="bg-[#0F172A] border-b-4 border-[#D95800]">
        <div className="max-w-5xl mx-auto px-4 py-12">
          <div className="flex items-center gap-2 mb-3">
            <GraduationCap size={20} weight="bold" className="text-[#D95800]" />
            <span className="text-[10px] font-black uppercase tracking-widest text-[#D95800]">Tutorials</span>
          </div>
          <h1 className="text-3xl md:text-5xl font-black uppercase tracking-tight text-white leading-tight">
            Learn Every Feature<br />In Minutes
          </h1>
          <p className="text-neutral-400 mt-3 max-w-xl text-sm md:text-base">
            Step-by-step guides to posting jobs, getting paid safely, winning work, and buying & selling on the marketplace.
          </p>
          {/* Search */}
          <div className="relative max-w-md mt-6">
            <MagnifyingGlass size={18} weight="bold" className="absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-500" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search tutorials... (e.g. escrow, offers, invoice)"
              className="w-full bg-white dark:bg-neutral-800 border-2 border-black rounded-xl pl-10 pr-4 py-3 text-sm font-semibold text-neutral-900 dark:text-white placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-[#D95800]"
              data-testid="tutorials-search-input"
            />
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Role filter */}
        <div className="flex flex-wrap gap-2 mb-6" data-testid="tutorials-role-filter">
          <button
            onClick={() => setSearchParams({})}
            className={`text-xs font-black uppercase px-3 py-1.5 border-2 border-black rounded-full ${!role ? 'bg-black text-white' : 'bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300'}`}
            data-testid="tutorials-filter-all"
          >
            All
          </button>
          {ROLES.map((r) => (
            <button
              key={r.key}
              onClick={() => setSearchParams({ role: r.key })}
              className={`text-xs font-black uppercase px-3 py-1.5 border-2 border-black rounded-full ${role === r.key ? 'bg-black text-white' : 'bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300'}`}
              data-testid={`tutorials-filter-${r.key}`}
            >
              {r.label}
            </button>
          ))}
        </div>

        {filtered.length === 0 ? (
          <div className="text-center py-16" data-testid="tutorials-empty">
            <p className="font-black uppercase text-neutral-700 dark:text-neutral-300">No tutorials match "{query}"</p>
            <p className="text-sm text-neutral-500 mt-1">Try a different keyword or clear the filters.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="tutorials-grid">
            {filtered.map((t) => {
              const rm = roleMeta(t.role);
              return (
                <Link
                  key={t.slug}
                  to={`/tutorials/${t.slug}`}
                  className="group bg-white dark:bg-neutral-800 border-2 border-black rounded-xl p-5 shadow-[4px_4px_0px_#000] hover:-translate-y-1 transition-transform"
                  data-testid={`tutorial-card-${t.slug}`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`${rm.color} text-white text-[10px] font-black uppercase px-2 py-0.5 border border-black rounded`}>{rm.label}</span>
                    <span className="text-[11px] text-neutral-400 flex items-center gap-1">
                      <Clock size={12} weight="bold" /> {t.minutes} min read
                    </span>
                  </div>
                  <h2 className="text-lg font-black text-neutral-900 dark:text-white leading-snug group-hover:text-[#D95800] transition-colors">{t.title}</h2>
                  <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-2">{t.summary}</p>
                  <span className="inline-flex items-center gap-1 text-xs font-black uppercase text-[#D95800] mt-3">
                    {t.steps.length} steps <ArrowRight size={13} weight="bold" />
                  </span>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
